"use client";

import * as React from "react";
import { Quote, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const testimonials = [{
  author: "Sarah M.",
  position: "Homeowner in Stittsville – Basement cleanout",
  quote: "JUNKR made clearing out our basement so easy. The team was professional, fast, and left the space spotless.",
  impact: "5-Star Service",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_basement_cleanout_team.png"
}, {
  author: "David K.",
  position: "Property Manager in Kanata – Estate clearing",
  quote: "Reliable same-day service that I can count on for senior living and commercial properties. Best junk removal in Ottawa.",
  impact: "Eco-Friendly",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/professional_junk_removal_truck_ottawa.png"
}, {
  author: "James L.",
  position: "Homeowner in Orleans – Garage decluttering",
  quote: "The crew arrived right on time and handled everything. It's amazing how much space we have back now. Highly recommend!",
  impact: "Fast & Efficient",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_garage_cleanup_service.png"
}, {
  author: "Elena R.",
  position: "Resident in Nepean – Appliance removal",
  quote: "I had an old fridge and stove that needed to go. JUNKR took them away in minutes without a single scratch on my floors.",
  impact: "Careful Handling",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_appliance_removal_specialists.png"
}, {
  author: "Marcus T.",
  position: "Business Owner in Barrhaven – Office cleanout",
  quote: "Professional service from start to finish. They handled our office furniture removal with zero disruption to our workday.",
  impact: "Commercial Grade",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/barrhaven_commercial_junk_removal.png"
}];
export default function ClientSuccessStories() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  React.useEffect(() => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    api.on("select", () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    });
  }, [api]);
  return <section data-section-id="238561" id="real-stories-from-ottawa-neighbors" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight mb-4 text-foreground">
              Trusted by 500+ Ottawa Homeowners
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl font-default">
              See why homeowners and businesses across Kanata, Nepean, Orleans, and the greater Ottawa area trust JUNKR for same‑day service, eco‑friendly disposal, and transparent pricing.
            </p>
          </div>
          
          <div className="flex items-center gap-6 shrink-0">
            <Button variant="ghost" size="lg" className="text-base font-medium hover:bg-transparent hover:text-primary transition-colors px-0 h-auto text-foreground" onClick={() => api?.scrollPrev()} disabled={!canScrollPrev}>
              <ArrowLeft className="mr-2 size-5" />
              Prev
            </Button>
            <Button variant="ghost" size="lg" className="text-base font-medium hover:bg-transparent hover:text-primary transition-colors px-0 h-auto text-foreground" onClick={() => api?.scrollNext()} disabled={!canScrollNext}>
              Next
              <ArrowRight className="ml-2 size-5" />
            </Button>
          </div>
        </div>

        <Carousel setApi={setApi} className="w-full">
          <CarouselContent>
            {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                  <div className="lg:col-span-5 relative">
                    <div className="relative aspect-square md:aspect-[4/5] overflow-hidden rounded-2xl shadow-xl bg-muted">
                      <img src={item.image} data-wvc-srcset={`${item.image}-wvc-srcset`} data-wvc-sizes={`${item.image}-wvc-sizes`} alt={`Review from ${item.author}`} className="object-cover w-full h-full contrast-125 saturate-110" loading="lazy" />
                      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junk_texture_overlay.png)] opacity-20 mix-blend-soft-light pointer-events-none"></div>
                    </div>
                    <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-primary/10 rounded-lg blur-3xl -z-10"></div>
                  </div>

                  <div className="lg:col-span-7 flex flex-col justify-center py-8 lg:py-0">
                    <Quote className="size-16 md:size-24 text-primary mb-6 md:mb-10 opacity-100" />
                    
                    <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-10 font-default text-foreground">
                      "{item.quote}"
                    </blockquote>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t border-border pt-8">
                      <div>
                        <div className="font-bold text-xl font-default text-foreground">
                          {item.author}
                        </div>
                        <div className="text-muted-foreground text-base mt-1 font-default">
                          {item.position}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-3">
                        <Badge variant="secondary" className="text-sm px-4 py-2 rounded-lg shadow-md w-fit bg-secondary text-secondary-foreground font-bold">
                          
                        </Badge>
                        <Badge variant="default" className="text-sm px-5 py-2.5 rounded-lg shadow-md w-fit bg-primary text-primary-foreground">
                           Verified Review
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>
      </div>
    </section>;
}